import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_Employee_FindById_VM } from 'ab-api/web/employee/findById';

import type { RootState } from 'af-reducers';

import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { Button } from '@acceligentllc/storybook';

import * as EmployeeActions from 'af-actions/employee';

import EmployeePreview from './EmployeePreview';
import Loading from './Loading';
import ResourcePreviewNew from '../../Shared/ResourcePreviewNew';

interface PathParams {
	id: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const Preview: React.FC<Props> = (props) => {
	const { location: { state: { orgAlias } }, companyName, findById, match: { params: { id } }, history, findRelatedWorkOrders } = props;

	const [employee, setEmployee] = React.useState<Nullable<W_Employee_FindById_VM>>(null);

	// On mount
	React.useEffect(() => {
		const fetch = async () => {
			const _employee = await findById(+id);
			setEmployee(_employee);
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const breadcrumbs = React.useMemo(() => [
		{ label: 'Labor', url: CLIENT.COMPANY.RESOURCES.EMPLOYEE.LIST(orgAlias, companyName) },
		{ label: 'Preview' },
	], [companyName, orgAlias]);

	const onGoToList = React.useCallback(() => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.LIST(orgAlias, companyName));
	}, [companyName, history, orgAlias]);

	const onGoToEdit = React.useCallback(() => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.EDIT(`${id}`, orgAlias, companyName));
	}, [companyName, history, id, orgAlias]);

	const fetchWorkOrders = React.useCallback(async (tableRequestModel: TableQuery, startDate: Date, endDate: Date) => {
		return await findRelatedWorkOrders(+id, tableRequestModel, startDate, endDate);
	}, [findRelatedWorkOrders, id]);

	const isEditable = React.useMemo(() => !!employee?.isActive
		&& (employee.assignableToWO || employee.assignableAsPM || employee.assignableAsSI || employee.assignableAsTechnician),
		[employee?.assignableAsPM, employee?.assignableAsSI, employee?.assignableAsTechnician, employee?.assignableToWO, employee?.isActive]);

	const renderHeaderBtns = React.useCallback(() => {
		return (
			<>
				<div>
					<Button
						label="Back to Labor List"
						onClick={onGoToList}
						style="secondary"
					/>
				</div>
				{
					isEditable &&
					<Button
						label="Edit"
						onClick={onGoToEdit}
						style="secondary"
					/>
				}
			</>
		);
	}, [isEditable, onGoToEdit, onGoToList]);

	const renderPreview = React.useCallback(() => {
		if (!employee) {
			return null;
		}
		return <EmployeePreview employee={employee} />;
	}, [employee]);

	if (!employee) {
		return <Loading />;
	}
	return (
		<ResourcePreviewNew
			breadcrumbs={breadcrumbs}
			companyName={companyName}
			csvName={`${companyName}_${employee.formattedCode}_work_orders.csv`}
			fetchRelatedWorkOrders={fetchWorkOrders}
			headerTitle={employee.fullName}
			includeRelatedWorkOrders={true}
			renderBody={renderPreview}
			renderHeaderButtons={renderHeaderBtns}
			tableName={TableNameEnum.EMPLOYEE_WORK_ORDERS}
		/>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findById: EmployeeActions.findById,
		findRelatedWorkOrders: EmployeeActions.findRelatedWorkOrders,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(Preview);
